import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Spin, Tag } from 'antd';
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ReloadOutlined,
    ThunderboltOutlined,
    VideoCameraOutlined,
    ClockCircleOutlined,
    HomeOutlined,
    PlayCircleFilled
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { FINDING_STREAM_SERVER } from './config';
import './ProvidersStatus.css';

const ProvidersStatus = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchStatus = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${FINDING_STREAM_SERVER}/tc/health/providers`);
            setData(res.data);
            setLastUpdated(new Date());
        } catch (error) {
            console.error('Failed to fetch provider status:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
        // Auto refresh every 30 seconds
        const interval = setInterval(fetchStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    const getStatusColor = (status) => {
        return status === 'up' ? '#22c55e' : '#ef4444';
    };

    const getResponseTimeColor = (ms) => {
        if (ms < 500) return '#22c55e';
        if (ms < 1000) return '#eab308';
        return '#ef4444';
    };

    const formatTime = (date) => {
        if (!date) return '';
        return date.toLocaleTimeString();
    };

    if (loading && !data) {
        return (
            <div className="providers-container">
                <div className="providers-loading">
                    <Spin size="large" />
                    <span>Loading provider status...</span>
                </div>
            </div>
        );
    }

    const providers = data?.providers ? Object.entries(data.providers) : [];
    const totalLive = providers.reduce((sum, [, p]) => sum + (p.liveCount || 0), 0);

    return (
        <div className="providers-container">
            <header className="providers-header">
                <div className="providers-title">
                    <Link to="/" className="back-home">
                        <HomeOutlined />
                    </Link>
                    <h1>Provider Status</h1>
                    <Tag
                        color={data?.status === 'healthy' ? 'success' : 'error'}
                        className="system-status"
                    >
                        {data?.status === 'healthy' ? 'All Systems Operational' : 'Issues Detected'}
                    </Tag>
                </div>
                <div className="providers-actions">
                    {lastUpdated && (
                        <span className="last-updated">
                            <ClockCircleOutlined /> Updated {formatTime(lastUpdated)}
                        </span>
                    )}
                    <button
                        className="refresh-btn"
                        onClick={fetchStatus}
                        disabled={loading}
                    >
                        <ReloadOutlined spin={loading} />
                        Refresh
                    </button>
                </div>
            </header>

            {data?.summary && (
                <div className="providers-summary">
                    <div className="summary-card">
                        <div className="summary-icon up">
                            <CheckCircleFilled />
                        </div>
                        <div className="summary-info">
                            <span className="summary-value">{data.summary.up}</span>
                            <span className="summary-label">Online</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-icon down">
                            <CloseCircleFilled />
                        </div>
                        <div className="summary-info">
                            <span className="summary-value">{data.summary.down}</span>
                            <span className="summary-label">Offline</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-icon matches">
                            <VideoCameraOutlined />
                        </div>
                        <div className="summary-info">
                            <span className="summary-value">{data.summary.totalMatches?.toLocaleString()}</span>
                            <span className="summary-label">Total Matches</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-icon live">
                            <PlayCircleFilled />
                        </div>
                        <div className="summary-info">
                            <span className="summary-value">{totalLive}</span>
                            <span className="summary-label">Live Now</span>
                        </div>
                    </div>
                    <div className="summary-card">
                        <div className="summary-icon speed">
                            <ThunderboltOutlined />
                        </div>
                        <div className="summary-info">
                            <span className="summary-value">{data.summary.avgResponseTime}ms</span>
                            <span className="summary-label">Avg Response</span>
                        </div>
                    </div>
                </div>
            )}

            <div className="providers-grid">
                {providers.map(([code, provider]) => (
                    <div
                        key={code}
                        className={`provider-card ${provider.status === 'up' ? 'up' : 'down'}`}
                    >
                        <div className="provider-header">
                            <div className="provider-name">
                                <span className="provider-code">{code}</span>
                                <span className="provider-fullname">{provider.fullName}</span>
                            </div>
                            <div
                                className="provider-status"
                                style={{ color: getStatusColor(provider.status) }}
                            >
                                {provider.status === 'up' ? (
                                    <CheckCircleFilled />
                                ) : (
                                    <CloseCircleFilled />
                                )}
                            </div>
                        </div>
                        <div className="provider-stats">
                            <div className="stat">
                                <VideoCameraOutlined />
                                <span className="stat-value">{provider.matchCount}</span>
                                <span className="stat-label">matches</span>
                            </div>
                            {provider.liveCount > 0 ? (
                                <div className="stat live">
                                    <span className="stat-value">{provider.liveCount}</span>
                                    <span className="stat-label">live</span>
                                </div>
                            ) : (
                                <div />
                            )}
                            <div className="stat">
                                <ThunderboltOutlined />
                                <span
                                    className="stat-value"
                                    style={{ color: getResponseTimeColor(provider.responseTime) }}
                                >
                                    {provider.responseTime}ms
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProvidersStatus;
